import { print } from 'graphql';

import {
  getAuthDashMetaCsrfToken,
  getImpersonationStateSnapshot,
  getLoggedInUserSnapshot,
  MetadataKey,
  selectIsImpersonating,
} from '@revelio/auth';
import { HttpError, UpdateUserDocument } from '@revelio/data-access';
import { MAX_PRIMARY_ENTITY_SELECTIONS } from '@revelio/iso-utility';

import { environment } from '../../../environments/environment';
import { TOO_MANY_CHART_ENTITIES_ERROR_MESSAGE } from '../../../next/CustomDash/filters/constants/filter-constants';
import { dashMetaJson } from '../../api/dash-meta-fetch';
import {
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../types';
import { filterPreferencesForScope } from '../utils/preference-helpers';

enum EntityType {
  CLIENT = 'client',
  USER = 'user',
}

// Types matching the dash-meta API
export interface UserPreference {
  id?: string;
  entityId: string;
  entityType: EntityType;
  scope: PreferenceScope;
  preferences: Record<string, string>;
  createdAt?: string;
  updatedAt?: string;
}

interface CreateUserPreferenceDto {
  entityType: EntityType;
  scope: PreferenceScope;
  preferences: Record<string, string>;
}

interface UpdateUserPreferenceDto {
  entityType?: EntityType;
  scope?: PreferenceScope;
  preferences?: Record<string, string>;
}

interface KeyValue {
  key: string;
  value: string;
}

// Replicate currency preference to user metadata (delete after full transition to user preferences)
async function syncCurrencyToUserDetails(code: string): Promise<void> {
  try {
    const { id: userId } = getLoggedInUserSnapshot() ?? {};
    if (!userId) return;

    const mutation = print(UpdateUserDocument);
    const variables = {
      id: userId,
      metadata: [{ key: MetadataKey.CurrencyCode, value: code }] as KeyValue[],
    };

    // The Go API, not dash-meta, so it cannot go through dashMetaJson.
    const response = await fetch(`${environment.GO_API_ROOT}/query`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': getAuthDashMetaCsrfToken() ?? '',
      },
      body: JSON.stringify({
        operationName: 'UpdateUser',
        query: mutation,
        variables,
      }),
    });
    if (!response.ok) {
      throw new HttpError(
        (await response.text()) || 'Unknown error',
        response.status,
        response.statusText
      );
    }
  } catch (err) {
    // Non-critical – log and continue without blocking preference save
    console.warn('Failed to sync currency preference to user details', err);
  }
}

// Convert UserPreferences to string-based preferences for API
function serializePreferences(
  preferences: Partial<UserPreferences>
): Record<string, string> {
  const result: Record<string, string> = {};

  Object.entries(preferences).forEach(([key, value]) => {
    if (value !== undefined) {
      result[key] = JSON.stringify(value);
    }
  });

  return result;
}

// Convert string-based preferences from API to UserPreferences
function deserializePreferences(
  preferences: Record<string, string>
): Partial<UserPreferences> {
  const result: Record<string, unknown> = {};

  Object.entries(preferences).forEach(([key, value]) => {
    try {
      result[key] = JSON.parse(value);
    } catch {
      result[key] = value;
    }
  });

  return result as Partial<UserPreferences>;
}

// Get all preferences for the current user, or for the impersonated user
// through dash-meta's admin-only read.
const fetchPreferences = (
  impersonatedUserId?: string | null
): Promise<UserPreference[]> =>
  dashMetaJson<{ success: boolean; data: UserPreference[] }>(
    impersonatedUserId
      ? `/api/user-preferences/all?entityId=${encodeURIComponent(impersonatedUserId)}`
      : '/api/user-preferences'
  ).then((response) => response.data);

// Get preferences by scope
const getPreferencesByScope = async (
  scope: PreferenceScope
): Promise<UserPreference | null> => {
  const preferences = await fetchPreferences();
  return preferences.find((p) => p.scope === scope) || null;
};

// Create new preferences
const createPreferences = async (
  scope: PreferenceScope,
  preferences: Partial<UserPreferences>
): Promise<UserPreference> => {
  const dto: CreateUserPreferenceDto = {
    entityType: EntityType.USER,
    scope,
    preferences: serializePreferences(
      filterPreferencesForScope(scope, preferences)
    ),
  };

  return dashMetaJson<{ success: boolean; data: UserPreference }>(
    '/api/user-preferences',
    {
      method: 'POST',
      body: JSON.stringify(dto),
    }
  ).then((response) => response.data);
};

// Update preferences by ID
const updatePreferences = async (
  id: string,
  preferences: Partial<UserPreferences>
): Promise<UserPreference> => {
  const dto: UpdateUserPreferenceDto = {
    preferences: serializePreferences(preferences),
  };

  return dashMetaJson<{ success: boolean; data: UserPreference }>(
    `/api/user-preferences/${id}`,
    {
      method: 'POST',
      body: JSON.stringify(dto),
    }
  ).then((response) => response.data);
};

// Save preferences for a scope (create or update)
export const savePreferencesForScope = async (
  scope: PreferenceScope,
  preferences: Partial<UserPreferences>
): Promise<UserPreference> => {
  // dash-meta saves to the session's own user, so a save while impersonating
  // would land on the admin's record.
  if (selectIsImpersonating(getImpersonationStateSnapshot())) {
    throw new Error('Preferences are read-only while impersonating');
  }

  const defaultFilters = preferences[UserPreferenceKey.DefaultFilters];
  if (defaultFilters && defaultFilters.length > MAX_PRIMARY_ENTITY_SELECTIONS) {
    throw new Error(TOO_MANY_CHART_ENTITIES_ERROR_MESSAGE);
  }

  const existing = await getPreferencesByScope(scope);

  // Replicate currency preference to user metadata (delete after full transition to user preferences)
  if (scope === PreferenceScope.GLOBAL) {
    const currencyCode = preferences[UserPreferenceKey.Currency]?.code;
    const existingPreferences = deserializePreferences(
      existing?.preferences || {}
    );
    const existingCurrencyCode =
      existingPreferences?.[UserPreferenceKey.Currency]?.code;
    if (currencyCode && currencyCode !== existingCurrencyCode) {
      syncCurrencyToUserDetails(currencyCode);
    }
  }

  if (existing?.id) {
    return updatePreferences(
      existing.id,
      filterPreferencesForScope(scope, preferences)
    );
  } else {
    return createPreferences(scope, preferences);
  }
};

/**
 * Fetch all preferences once and return them grouped by scope (deserialized).
 * This is used by UI code that needs to switch scopes without refetching.
 */
export const getAllPreferences = async (
  impersonatedUserId?: string | null
): Promise<Record<PreferenceScope, Partial<UserPreferences>>> => {
  const all = await fetchPreferences(impersonatedUserId);

  const byScope: Record<PreferenceScope, Partial<UserPreferences>> = {
    [PreferenceScope.GLOBAL]: {},
    [PreferenceScope.INDIVIDUAL]: {},
    [PreferenceScope.COMPANY]: {},
    [PreferenceScope.INDUSTRY]: {},
    [PreferenceScope.GEOGRAPHY]: {},
    [PreferenceScope.ROLE]: {},
    [PreferenceScope.SKILL]: {},
    [PreferenceScope.ACTIVITY]: {},
  };

  all.forEach((pref) => {
    byScope[pref.scope] = filterPreferencesForScope(
      pref.scope,
      deserializePreferences(pref.preferences)
    );
  });

  return byScope;
};
